import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../contexts/ThemeContext';
import { Sun, Moon, Gamepad2, Users, Trophy, LayoutDashboard, Star, ShieldCheck } from 'lucide-react';
import { avatarSrc } from '../avatar/avatarList';
import './Header.css';

const Header = () => {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [hidden, setHidden] = useState(false);

  useEffect(() => { setMobileOpen(false); }, [location]);

  useEffect(() => {
    let lastY = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      setHidden(y > lastY && y > 80);
      lastY = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <header className={`header${hidden ? ' header--hidden' : ''}`}>
      <div className="header-inner">

        {/* Logo */}
        <Link to="/" className="logo">
          <div className="logo-mark">
            <img src="/logo/logo.png" alt="MapHunt logo" />
          </div>
          <span className="logo-text">Map<span className="logo-accent">Hunt</span></span>
        </Link>

        {/* Desktop Nav */}
        <nav className="nav-desktop" aria-label="Main navigation">
          {user ? (
            <>
              <Link to="/dashboard"   className={`nav-link ${isActive('/dashboard')   ? 'active' : ''}`}><LayoutDashboard size={15}/>Dashboard</Link>
              <Link to="/game"        className={`nav-link ${isActive('/game')        ? 'active' : ''}`}><Gamepad2 size={15}/>Play</Link>
              <Link to="/multiplayer" className={`nav-link ${isActive('/multiplayer') ? 'active' : ''}`}><Users size={15}/>Multiplayer</Link>
              <Link to="/leaderboard" className={`nav-link ${isActive('/leaderboard') ? 'active' : ''}`}><Trophy size={15}/>Leaderboard</Link>
              {user.role === 'admin' && (
                <Link to="/admin/dashboard" className="nav-link nav-admin"><ShieldCheck size={15}/>Admin</Link>
              )}
            </>
          ) : (
            <Link to="/leaderboard" className={`nav-link ${isActive('/leaderboard') ? 'active' : ''}`}><Trophy size={15}/>Leaderboard</Link>
          )}
        </nav>

        {/* Right side */}
        <div className="header-right">
          {/* Theme toggle */}
          <button className="icon-btn" onClick={toggle} aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}>
            {theme === 'dark' ? <Sun size={17} /> : <Moon size={17} />}
          </button>

          {user ? (
            <div className="user-pill">
              <Link to="/profile" className="user-pill-link">
                <div className="user-avatar" aria-hidden="true">
                  {user.avatar
                    ? <img src={avatarSrc(user.avatar)} alt="" />
                    : user.name?.charAt(0).toUpperCase()}
                </div>
                <div className="user-info">
                  <span className="user-name">{user.name?.split(' ')[0]}</span>
                  <span className="user-score"><Star size={10} />{(user.total_score || 0).toLocaleString()}</span>
                </div>
              </Link>
              <button className="logout-btn" onClick={handleLogout}>Out</button>
            </div>
          ) : (
            <div className="auth-btns">
              <Link to="/login"    className="btn-ghost-sm">Log in</Link>
              <Link to="/register" className="btn-brand-sm">Sign up</Link>
            </div>
          )}

          {/* Mobile hamburger */}
          <button
            className={`hamburger ${mobileOpen ? 'open' : ''}`}
            onClick={() => setMobileOpen(v => !v)}
            aria-label="Toggle menu"
            aria-expanded={mobileOpen}
          >
            <span /><span /><span />
          </button>
        </div>
      </div>

      {/* Mobile nav */}
      {mobileOpen && (
        <nav className="nav-mobile" aria-label="Mobile navigation">
          {user ? (
            <>
              <Link to="/dashboard"   className={`mob-link ${isActive('/dashboard')   ? 'active' : ''}`}><LayoutDashboard size={16}/>Dashboard</Link>
              <Link to="/game"        className={`mob-link ${isActive('/game')        ? 'active' : ''}`}><Gamepad2 size={16}/>Play Solo</Link>
              <Link to="/multiplayer" className={`mob-link ${isActive('/multiplayer') ? 'active' : ''}`}><Users size={16}/>Multiplayer</Link>
              <Link to="/leaderboard" className={`mob-link ${isActive('/leaderboard') ? 'active' : ''}`}><Trophy size={16}/>Leaderboard</Link>
              {user.role === 'admin' && (
                <Link to="/admin/dashboard" className="mob-link nav-admin"><ShieldCheck size={16}/>Admin</Link>
              )}
              <button className="mob-link mob-logout" onClick={handleLogout}>Log out</button>
            </>
          ) : (
            <>
              <Link to="/leaderboard" className="mob-link"><Trophy size={16}/>Leaderboard</Link>
              <Link to="/login"       className="mob-link">Log in</Link>
              <Link to="/register"    className="mob-link mob-signup">Sign up free</Link>
            </>
          )}
        </nav>
      )}
    </header>
  );
};

export default Header;
